import { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { calculatePaces, EVENTS, applyAltitudeAdjustment, pickPaceBenchmark } from '../../lib/vdot';
import { resolvePaceTags, renderWorkoutText } from '../../lib/paceTags';
import { RecruitingView } from '../../components/RecruitingView';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getWeekDates(referenceDate) {
  const d = new Date(referenceDate);
  const day = d.getDay();
  const diff = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - diff);
  return Array.from({ length: 7 }, (_, i) => {
    const dt = new Date(d);
    dt.setDate(d.getDate() + i);
    return dt;
  });
}

function toDateStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function daysUntil(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const race = new Date(dateStr + 'T00:00:00');
  return Math.round((race - today) / (1000 * 60 * 60 * 24));
}

function formatRaceDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Multi-day aware date formatter — collapses same-month ranges. */
function formatRaceDateRange(startStr, endStr) {
  if (!startStr) return '';
  if (!endStr || endStr === startStr) return formatRaceDate(startStr);
  const start = new Date(startStr + 'T00:00:00');
  const end = new Date(endStr + 'T00:00:00');
  const sameMonth =
    start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
  if (sameMonth) {
    const mon = start.toLocaleDateString('en-US', { month: 'short' });
    return `${mon} ${start.getDate()}–${end.getDate()}, ${start.getFullYear()}`;
  }
  const s = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const e = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${s} – ${e}`;
}

function getEventLabels(eventField) {
  if (!eventField) return '';
  try {
    const parsed = JSON.parse(eventField);
    if (Array.isArray(parsed)) return parsed.map(e => EVENTS[e]?.label ?? e).join(', ');
  } catch {}
  return EVENTS[eventField]?.label ?? eventField;
}

export function ParentView() {
  const { profile } = useAuth();

  // All linked athletes
  const [linkedAthletes, setLinkedAthletes] = useState(null); // null = loading
  const [selectedAthleteId, setSelectedAthleteId] = useState(null);

  // Per-athlete data
  const [athlete, setAthlete] = useState(null);
  const [weekWorkouts, setWeekWorkouts] = useState([]);
  const [hasPlan, setHasPlan] = useState(false);
  const [upcomingRaces, setUpcomingRaces] = useState([]);
  const [athleteLoading, setAthleteLoading] = useState(false);
  const [recruitingEligible, setRecruitingEligible] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Step 1: load all linked athlete IDs + names
  useEffect(() => {
    if (profile) loadLinkedAthletes();
  }, [profile]);

  async function loadLinkedAthletes() {
    const { data: links } = await supabase
      .from('parent_athlete_links')
      .select('athlete_id')
      .eq('parent_id', profile.id);

    if (!links?.length) {
      setLinkedAthletes([]);
      return;
    }

    const ids = links.map(l => l.athlete_id);
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .in('id', ids)
      .order('full_name');

    setLinkedAthletes(profiles ?? []);
    setSelectedAthleteId(profiles?.[0]?.id ?? null);
  }

  // Step 2: load data for the selected athlete
  useEffect(() => {
    if (selectedAthleteId) loadAthleteData(selectedAthleteId);
  }, [selectedAthleteId]);

  async function loadAthleteData(athleteId) {
    setAthleteLoading(true);
    setUpcomingRaces([]);
    setWeekWorkouts([]);
    setRecruitingEligible(false);
    setActiveTab('overview');

    const athleteProf = linkedAthletes?.find(a => a.id === athleteId) ?? null;
    setAthlete(athleteProf);

    await Promise.all([
      loadNextRace(athleteId),
      loadWeek(athleteId),
      loadRecruitingEligibility(athleteId),
    ]);

    setAthleteLoading(false);
  }

  async function loadRecruitingEligibility(athleteId) {
    const [{ data: ap }, { data: billing }] = await Promise.all([
      supabase.from('athlete_profiles').select('grade_level').eq('id', athleteId).maybeSingle(),
      supabase.from('billing').select('tier').eq('athlete_id', athleteId)
        .order('year', { ascending: false }).order('month', { ascending: false }).limit(1),
    ]);
    const gradeOk = ['10th', '11th', '12th'].includes(ap?.grade_level);
    const tierOk = billing?.[0]?.tier === 'elite';
    setRecruitingEligible(!!(gradeOk && tierOk));
  }


  async function loadNextRace(athleteId) {
    const today = new Date().toISOString().split('T')[0];
    const { data } = await supabase
      .from('races')
      .select('id, name, race_date, race_date_end, event, location, priority, notes, result_time_seconds, relay_leg')
      .eq('athlete_id', athleteId)
      .gte('race_date', today)
      .order('race_date', { ascending: true })
      .limit(10);
    setUpcomingRaces(data ?? []);
  }

  async function loadWeek(athleteId) {
    const today = new Date();
    const weekDates = getWeekDates(today);
    const todayStr = toDateStr(today);

    const { data: assignments } = await supabase
      .from('athlete_plan_assignments')
      .select('plan_id, plans(id, month, year)')
      .eq('athlete_id', athleteId);

    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();
    const match = assignments?.find(a =>
      a.plans?.month === currentMonth && a.plans?.year === currentYear
    );

    if (!match) {
      setHasPlan(false);
      setWeekWorkouts(weekDates.map((d, i) => ({
        date: toDateStr(d),
        dayLabel: DAY_LABELS[i],
        dayNum: d.getDate(),
        workout: '',
        isToday: toDateStr(d) === todayStr,
      })));
      return;
    }

    setHasPlan(true);
    const dateStrings = weekDates.map(toDateStr);
    const { data: planWorkouts } = await supabase
      .from('plan_workouts')
      .select('workout_date, custom_description')
      .eq('plan_id', match.plans.id)
      .in('workout_date', dateStrings);

    const workoutMap = {};
    (planWorkouts ?? []).forEach(w => { workoutMap[w.workout_date] = w.custom_description ?? ''; });

    const [{ data: benchmarks }, { data: apAlt }] = await Promise.all([
      supabase.from('benchmarks').select('id, event, time_seconds')
        .eq('athlete_id', athleteId).order('race_date', { ascending: false }).limit(20),
      supabase.from('athlete_profiles').select('training_altitude_ft, pinned_benchmark_id')
        .eq('id', athleteId).maybeSingle(),
    ]);

    let resolvedPaces = null;
    const bench = pickPaceBenchmark(benchmarks, apAlt?.pinned_benchmark_id);
    if (bench) {
      const result = calculatePaces(bench.event, bench.time_seconds);
      resolvedPaces = result?.paces ?? null;
      if (resolvedPaces) resolvedPaces = applyAltitudeAdjustment(resolvedPaces, apAlt?.training_altitude_ft ?? null);
    }

    setWeekWorkouts(weekDates.map((d, i) => {
      const dateStr = toDateStr(d);
      const rawText = workoutMap[dateStr] ?? '';
      return {
        date: dateStr,
        dayLabel: DAY_LABELS[i],
        dayNum: d.getDate(),
        workout: resolvedPaces ? resolvePaceTags(rawText, resolvedPaces) : rawText,
        isToday: dateStr === todayStr,
      };
    }));
  }

  const initials = athlete?.full_name
    ? athlete.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  // Still loading linked athletes list
  if (linkedAthletes === null) {
    return (
      <Layout>
        <div className="loading-screen" style={{ minHeight: 200 }}>
          <div className="loading-dot-row">
            <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
          </div>
        </div>
      </Layout>
    );
  }

  if (linkedAthletes.length === 0) {
    return (
      <Layout>
        <div style={{ marginBottom: 24 }}>
          <p className="page-eyebrow">Parent View</p>
          <h1 className="page-title">Welcome, {profile?.full_name?.split(' ')[0]}.</h1>
        </div>
        <div className="empty-state">
          <p className="empty-state-title">Not linked to an athlete</p>
          <p className="empty-state-sub">Ask your athlete's coach or the athlete to link your account by email.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <p className="page-eyebrow">Parent View</p>
          <h1 className="page-title">Welcome, {profile?.full_name?.split(' ')[0]}.</h1>
        </div>

        {/* Athlete switcher */}
        {linkedAthletes.length > 1 && (
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Viewing athlete</label>
            <select
              className="form-input form-select"
              style={{ minWidth: 200 }}
              value={selectedAthleteId ?? ''}
              onChange={e => setSelectedAthleteId(e.target.value)}
            >
              {linkedAthletes.map(a => (
                <option key={a.id} value={a.id}>{a.full_name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {athleteLoading ? (
        <div className="loading-screen" style={{ minHeight: 200 }}>
          <div className="loading-dot-row">
            <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
          </div>
        </div>
      ) : (
        <>
          {/* Athlete header */}
          <div className="profile-header" style={{ marginBottom: 20 }}>
            <div className="profile-avatar">{initials}</div>
            <div className="profile-meta">
              <h2>{athlete?.full_name}</h2>
              <p>{athlete?.email}</p>
            </div>
          </div>

          {/* Tab bar — Recruiting only shown for 10th–12th grade Elite athletes */}
          {recruitingEligible && (
            <div className="rec-section-tabs" style={{ marginBottom: 20, borderBottom: '2px solid var(--border)' }}>
              <button
                className={`rec-page-tab${activeTab === 'overview' ? ' rec-page-tab--active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >Overview</button>
              <button
                className={`rec-page-tab${activeTab === 'recruiting' ? ' rec-page-tab--active' : ''}`}
                onClick={() => setActiveTab('recruiting')}
              >Recruiting</button>
            </div>
          )}

          {activeTab === 'overview' && (
            <>
              {/* Upcoming Races */}
              <div className="section-card" style={{ marginBottom: 20 }}>
                <div className="section-card-header">
                  <span className="section-card-title">Upcoming Races</span>
                </div>
                {upcomingRaces.length === 0 ? (
                  <div className="section-card-body" style={{ color: 'var(--gray-400)', fontSize: 13 }}>
                    No upcoming races scheduled.
                  </div>
                ) : (
                  <div className="race-list">
                    {upcomingRaces.map(race => {
                      const days = daysUntil(race.race_date);
                      return (
                        <div key={race.id} className="race-item">
                          <div className={`race-priority-badge priority-${race.priority}`}>{race.priority}</div>
                          <div className="race-item-main">
                            <div className="race-item-name">{race.name}</div>
                            <div className="race-item-meta">
                              {getEventLabels(race.event)}
                              {race.location && ` · ${race.location}`}
                            </div>
                            {race.notes && <div className="race-item-meta" style={{ fontStyle: 'italic' }}>{race.notes}</div>}
                          </div>
                          <div className="race-item-right">
                            <div className="race-item-date">{formatRaceDateRange(race.race_date, race.race_date_end)}</div>
                            <div className={`race-countdown${days <= 7 ? ' race-countdown--soon' : ''}`}>
                              {days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days}d`}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* This Week */}
              <div className="section-card" style={{ marginBottom: 20 }}>
                <div className="section-card-header">
                  <span className="section-card-title">This Week's Training</span>
                  {!hasPlan && <span className="cell-muted" style={{ fontSize: 12 }}>No plan assigned yet</span>}
                </div>
                <div className="week-grid">
                  {weekWorkouts.map(day => (
                    <div key={day.date} className={`week-day-card${day.isToday ? ' week-day-card--today' : ''}`}>
                      <div className="week-day-name">{day.dayLabel}</div>
                      <div className="week-day-date">{day.dayNum}</div>
                      <div className="week-day-workout">
                        {day.workout ? renderWorkoutText(day.workout) : <span className="cell-muted">Rest</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {activeTab === 'recruiting' && recruitingEligible && (
            <RecruitingView athleteId={selectedAthleteId} athleteName={athlete?.full_name} />
          )}

        </>
      )}
    </Layout>
  );
}
